'use client';

import { c as _c } from "react/compiler-runtime";
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import { FlexColumn, FlexRow } from 'src/components/Atoms/FlexBox';
import Drawer from '@mui/material/Drawer';
import { useDataProvider } from '@effektify/data-provider';
import LiveHelpOutlinedIcon from '@mui/icons-material/LiveHelpOutlined';
import DrawerContent from '../DrawerContent/DrawerContent';
import { useReportHelpContent } from 'src/hooks/useReportHelpContent';
import { Content } from 'src/types/content';
import GlossarySection from 'src/components/Pages/KnowledgeHub/components/GlossarySection';
import { MediaCard } from 'src/components/Pages/KnowledgeHub/components/MediaCard';
import KnowledgeHubArticle from 'src/components/Pages/KnowledgeHub/components/KnowledgeHubArticle';
import { DrawerContentItem } from '../DrawerContentItem/DrawerContentItem';
import useTranslations from 'src/hooks/useTranslations';
import Typography from '@mui/material/Typography';
import { Tooltip } from 'src/components/Atoms/Tooltip';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useVariables } from '@effektify/engine';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
const ContentDrawer = styled(Drawer)(({
  theme
}) => ({
  width: '53rem',
  zIndex: theme.zIndex.drawer + 2,
  position: 'absolute',
  top: theme.sizes.spacing['05'],
  right: theme.sizes.spacing['05'],
  height: 'auto',
  maxHeight: 'calc(100vh - 7rem)'
}));
const MediaList = styled(Box)(({
  theme
}) => ({
  gap: theme.sizes.spacing['07'],
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  width: '100%'
}));
const ListBox = styled(FlexRow)(({
  theme
}) => ({
  paddingBottom: theme.sizes.spacing['04'],
  paddingTop: theme.sizes.spacing['04'],
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: 0,
  borderTop: `1px solid ${theme.colors.border.secondary}`,
  ':last-child': {
    borderBottom: `1px solid ${theme.colors.border.secondary}`
  }
}));
const SectionBox = styled(FlexColumn)(({
  theme
}) => ({
  width: '100%',
  gap: theme.sizes.spacing['07'],
  ':not(:last-child)': {
    marginBottom: theme.sizes.spacing['08'],
    paddingBottom: theme.sizes.spacing['08'],
    borderBottom: `1px solid ${theme.colors.border.secondary}`
  }
}));
export const ReportHelp: React.FC = () => {
  const $ = _c(91);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [subDrawerOpen, setSubDrawerOpen] = useState(false);
  const [storedSelectedKey, setStoredSelectedKey] = useState("");
  const {
    cmsTags,
    configurationHelp
  } = useDataProvider();
  const {
    hasMetConditions
  } = useVariables();
  let t0;
  if ($[0] !== cmsTags || $[1] !== hasMetConditions) {
    let t1;
    if ($[3] !== hasMetConditions) {
      t1 = (acc, t2) => {
        const [contentType, tags] = t2;
        const passedTags = tags.filter(tag => {
          if (!tag.conditions || tag.conditions.length === 0) {
            return true;
          }
          return tag.conditions.some(condition => hasMetConditions(condition));
        });
        if (passedTags.length > 0) {
          if (!acc[contentType]) {
            acc[contentType] = [];
          }
          acc[contentType].push(...passedTags.map(_temp));
        }
        return acc;
      };
      $[3] = hasMetConditions;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = Object.entries(cmsTags || {}).reduce(t1, {} as Record<string, string[]>);
    $[0] = cmsTags;
    $[1] = hasMetConditions;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const filteredCmsTags = t0;
  let t1;
  if ($[5] !== filteredCmsTags) {
    t1 = filteredCmsTags || {};
    $[5] = filteredCmsTags;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const {
    isLoading,
    content
  } = useReportHelpContent(t1);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const {
    t
  } = useTranslations();
  if (!cmsTags || Object.keys(cmsTags).length === 0 || isLoading || !content) {
    return null;
  }
  const configurationHelpContent = content[configurationHelp || ""];
  let t2;
  if ($[7] !== content) {
    t2 = Object.entries(content).filter(_temp2);
    $[7] = content;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const glossaries = t2 as [string, Content<'api::glossary-term.glossary-term'>][];
  let t3;
  if ($[9] !== glossaries) {
    t3 = glossaries.map(_temp3);
    $[9] = glossaries;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const glossaryContent = t3;
  let t4;
  if ($[11] !== content) {
    t4 = Object.entries(content).filter(_temp4);
    $[11] = content;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const kpis = t4 as [string, Content<'api::kpi.kpi'>][];
  let t5;
  if ($[13] !== kpis) {
    t5 = kpis.map(_temp5);
    $[13] = kpis;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const kpiContent = t5;
  let t6;
  if ($[15] !== content) {
    t6 = Object.entries(content).filter(_temp6);
    $[15] = content;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const reportHelp = t6 as [string, Content<'api::report-help.report-help'>][];
  let t7;
  if ($[17] !== configurationHelp || $[18] !== reportHelp) {
    t7 = reportHelp.filter(t8 => {
      const [key_2] = t8;
      return key_2 !== configurationHelp;
    }).map(_temp7);
    $[17] = configurationHelp;
    $[18] = reportHelp;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const reportHelpContent = t7;
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setDrawerOpen(true);
    };
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const onOpenHelpDrawer = t8;
  let t9;
  if ($[21] !== pathname || $[22] !== router || $[23] !== searchParams) {
    t9 = () => {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("helpKey");
      const path = newParams.size > 0 ? pathname + "?" + newParams.toString() : pathname;
      router.replace(path, {
        scroll: false
      });
      setSubDrawerOpen(false);
    };
    $[21] = pathname;
    $[22] = router;
    $[23] = searchParams;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const onCloseSubItem = t9;
  let t10;
  if ($[25] !== onCloseSubItem || $[26] !== pathname || $[27] !== router || $[28] !== searchParams) {
    t10 = () => {
      const newParams_0 = new URLSearchParams(searchParams);
      newParams_0.delete("helpKey");
      const path_0 = newParams_0.size > 0 ? pathname + "?" + newParams_0.toString() : pathname;
      router.replace(path_0, {
        scroll: false
      });
      setDrawerOpen(false);
      setStoredSelectedKey("");
      onCloseSubItem();
    };
    $[25] = onCloseSubItem;
    $[26] = pathname;
    $[27] = router;
    $[28] = searchParams;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const onCloseHelpDrawer = t10;
  let t11;
  if ($[30] !== glossaryContent || $[31] !== onCloseSubItem) {
    t11 = t12 => {
      const searchTerm = t12 === undefined ? "" : t12;
      setSubDrawerOpen(true);
      setSelectedComponent(<GlossarySection glossary={glossaryContent} onClose={() => onCloseSubItem()} search={searchTerm} />);
    };
    $[30] = glossaryContent;
    $[31] = onCloseSubItem;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const selectGlossary = t11;
  let t12;
  if ($[33] !== onCloseSubItem) {
    t12 = kpi => {
      setSubDrawerOpen(true);
      setSelectedComponent(<KnowledgeHubArticle item={kpi} onClose={() => onCloseSubItem()} />);
    };
    $[33] = onCloseSubItem;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const selectKpi = t12;
  let t13;
  if ($[35] !== onCloseSubItem) {
    t13 = reportHelp_0 => {
      setSubDrawerOpen(true);
      setSelectedComponent(<DrawerContent onClose={() => onCloseSubItem()} title={reportHelp_0.title || ""}><DrawerContentItem item={reportHelp_0} /></DrawerContent>);
    };
    $[35] = onCloseSubItem;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  const selectReportHelp = t13;
  const setSelectedComponentFromKey = helpKey => {
    if (helpKey.startsWith("glossary-term")) {
      const item = content[helpKey] as Content<'api::glossary-term.glossary-term'>;
      const searchTerm_0 = item?.displayName || "";
      selectGlossary(searchTerm_0);
      return;
    }
    const item_0 = content[helpKey];
    if (item_0) {
      if (helpKey.startsWith("kpi:")) {
        const reportKpiItem = item_0 as Content<'api::kpi.kpi'>;
        selectKpi(reportKpiItem);
      } else {
        if (helpKey.startsWith("report-help:")) {
          const reportHelpItem = item_0 as Content<'api::report-help.report-help'>;
          selectReportHelp(reportHelpItem);
        }
      }
    }
  };
  const helpKeyParam = searchParams.get("helpKey");
  const helpKey_0 = decodeURIComponent(helpKeyParam || "");
  if (helpKey_0 && helpKey_0 !== storedSelectedKey && Object.keys(content).includes(helpKey_0)) {
    setStoredSelectedKey(helpKey_0);
    setDrawerOpen(true);
    setSubDrawerOpen(true);
    setSelectedComponentFromKey(helpKey_0);
  }
  let t14;
  if ($[37] !== t) {
    t14 = t("reportPage.help.helpButtonText");
    $[37] = t;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => onOpenHelpDrawer();
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Button variant="outlined" onClick={t15} aria-label="help" sx={{
      padding: _temp8
    }}><LiveHelpOutlinedIcon /></Button>;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t14) {
    t17 = <Tooltip title={t14} placement="bottom">{t16}</Tooltip>;
    $[41] = t14;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== onCloseHelpDrawer) {
    t18 = () => onCloseHelpDrawer();
    $[43] = onCloseHelpDrawer;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const t19 = !drawerOpen;
  let t20;
  if ($[45] !== onCloseHelpDrawer) {
    t20 = () => onCloseHelpDrawer();
    $[45] = onCloseHelpDrawer;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t) {
    t21 = t("reportPage.help.title");
    $[47] = t;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      width: "100%"
    };
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== configurationHelpContent) {
    t23 = configurationHelpContent && <SectionBox><DrawerContentItem item={configurationHelpContent} /></SectionBox>;
    $[50] = configurationHelpContent;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== glossaryContent.length || $[53] !== selectGlossary || $[54] !== t) {
    t24 = glossaryContent.length > 0 && <SectionBox><Typography variant="heading-lg">{t("reportPage.help.reportHelpGlossaryHeader")}</Typography><Typography variant="body-base" sx={_temp9}>{t("reportPage.help.reportHelpGlossaryDescription")}</Typography><Button variant="contained" onClick={() => selectGlossary()} sx={{
        width: "fit-content"
      }}><MenuBookOutlinedIcon sx={{
          width: 20,
          height: 20
        }} />{t("reportPage.help.glossaryLink")}</Button></SectionBox>;
    $[52] = glossaryContent.length;
    $[53] = selectGlossary;
    $[54] = t;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== kpiContent || $[57] !== selectKpi || $[58] !== t) {
    t25 = kpiContent.length > 0 && <SectionBox><Typography variant="heading-lg">{t("reportPage.help.relevantKpis")}</Typography><MediaList>{kpiContent?.map((item_1, index) => <MediaCard key={item_1.documentId || index} title={item_1.title || ""} media={{
          thumbnailUrl: item_1.thumbnail?.url || item_1.media?.formats?.thumbnail?.url || null,
          alternativeText: item_1.thumbnail?.alternativeText || item_1.media?.alternativeText || item_1.title || ""
        }} onClick={() => {
          selectKpi(item_1);
        }} />)}</MediaList></SectionBox>;
    $[56] = kpiContent;
    $[57] = selectKpi;
    $[58] = t;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== reportHelpContent || $[61] !== selectReportHelp || $[62] !== t) {
    t26 = reportHelpContent.length > 0 && <SectionBox><Typography variant="heading-lg">{t("reportPage.help.reportHelpArticlesHeader")}</Typography><FlexColumn>{reportHelpContent.map(reportHelp_1 => <ListBox key={reportHelp_1.id} onClick={() => selectReportHelp(reportHelp_1)}><Typography variant="heading-md" sx={{
            color: _temp10
          }}>{reportHelp_1.title}</Typography><ArrowForwardIcon sx={{
            width: 20,
            height: 20
          }} /></ListBox>)}</FlexColumn></SectionBox>;
    $[60] = reportHelpContent;
    $[61] = selectReportHelp;
    $[62] = t;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t23 || $[65] !== t24 || $[66] !== t25 || $[67] !== t26) {
    t27 = <FlexColumn sx={t22}>{t23}{t24}{t25}{t26}</FlexColumn>;
    $[64] = t23;
    $[65] = t24;
    $[66] = t25;
    $[67] = t26;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== t20 || $[70] !== t21 || $[71] !== t27) {
    t28 = <DrawerContent onClose={t20} title={t21}>{t27}</DrawerContent>;
    $[69] = t20;
    $[70] = t21;
    $[71] = t27;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      width: "49rem",
      zIndex: _temp11
    };
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      paper: {
        sx: {
          width: "100%",
          maxWidth: "49rem"
        }
      }
    };
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] !== onCloseSubItem) {
    t31 = () => onCloseSubItem();
    $[75] = onCloseSubItem;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  const t32 = !subDrawerOpen;
  let t33;
  if ($[77] !== selectedComponent || $[78] !== subDrawerOpen || $[79] !== t31 || $[80] !== t32) {
    t33 = <ContentDrawer sx={t29} anchor="right" open={subDrawerOpen} slotProps={t30} onClose={t31} aria-hidden={t32}>{selectedComponent}</ContentDrawer>;
    $[77] = selectedComponent;
    $[78] = subDrawerOpen;
    $[79] = t31;
    $[80] = t32;
    $[81] = t33;
  } else {
    t33 = $[81];
  }
  let t34;
  if ($[82] !== drawerOpen || $[83] !== t18 || $[84] !== t19 || $[85] !== t28 || $[86] !== t33) {
    t34 = <ContentDrawer anchor="right" open={drawerOpen} onClose={t18} aria-hidden={t19}>{t28}{t33}</ContentDrawer>;
    $[82] = drawerOpen;
    $[83] = t18;
    $[84] = t19;
    $[85] = t28;
    $[86] = t33;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  let t35;
  if ($[88] !== t17 || $[89] !== t34) {
    t35 = <>{t17}{t34}</>;
    $[88] = t17;
    $[89] = t34;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  return t35;
};
export default ReportHelp;
function _temp(tag_0) {
  return tag_0.identifier;
}
function _temp2(t0) {
  const [key] = t0;
  return key.startsWith("glossary-term:");
}
function _temp3(t0) {
  const [, value] = t0;
  return value;
}
function _temp4(t0) {
  const [key_0] = t0;
  return key_0.startsWith("kpi:");
}
function _temp5(t0) {
  const [, value_0] = t0;
  return value_0;
}
function _temp6(t0) {
  const [key_1] = t0;
  return key_1.startsWith("report-help:");
}
function _temp7(t0) {
  const [, value_1] = t0;
  return value_1;
}
function _temp8(theme) {
  return theme.sizes.spacing["04"];
}
function _temp9(theme_0) {
  return {
    color: theme_0.colors.text.secondary
  };
}
function _temp10(theme_1) {
  return theme_1.colors.text.primary;
}
function _temp11(theme_2) {
  return theme_2.zIndex.drawer + 3;
}